import { Box, VStack, StackProps } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Children } from 'react';
import { spacing } from 'theme/tokens';

const MotionBox = motion(Box);

interface FormStackProps extends Omit<StackProps, 'spacing' | 'direction'> {
	/** Use 'section' for gaps between FormSections, 'field' (default) for gaps between fields */
	density?: 'field' | 'section';
	/** Animate children with staggered reveal on mount */
	animate?: boolean;
}

const fieldReveal = {
	hidden: { opacity: 0, y: 8 },
	visible: (i: number) => ({
		opacity: 1,
		y: 0,
		transition: {
			delay: i * 0.04,
			duration: 0.25,
			ease: [0.25, 0.1, 0.25, 1],
		},
	}),
};

/**
 * Consistent vertical stack for form fields.
 * Always use this instead of VStack/Flex for form layouts.
 */
export default function FormStack({
	density = 'field',
	animate = false,
	children,
	...rest
}: FormStackProps) {
	const gap = density === 'section' ? spacing.sectionGap : spacing.fieldGap;

	if (!animate) {
		return (
			<VStack spacing={gap} align="stretch" {...rest}>
				{children}
			</VStack>
		);
	}

	const items = Children.toArray(children);
	return (
		<VStack spacing={gap} align="stretch" {...rest}>
			{items.map((child, i) => (
				<MotionBox
					key={i}
					custom={i}
					initial="hidden"
					animate="visible"
					variants={fieldReveal}
				>
					{child}
				</MotionBox>
			))}
		</VStack>
	);
}
